<template>
  <div class="tc">
    <div v-if="showHeader" class="tc-head">
      <button type="button" class="tc-like" :class="{ on: liked }" :disabled="!uid || locked" :aria-pressed="liked" @click="toggleLike">
        {{ liked ? "♥" : "♡" }} {{ likeCount }}
      </button>
      <button type="button" class="tc-toggle" @click="open = !open">
        <span class="tc-count">💬 {{ count }} {{ count === 1 ? "comment" : "comments" }}</span>
        <span class="tc-chev">{{ open ? "▲" : "▼" }}</span>
      </button>
    </div>

    <div v-if="open" class="tc-body">
      <div v-if="locked" class="tc-locked">
        🔒 {{ count }} comments · <a class="tc-link" @click="$router.push('/membership')">subscribe to read</a>
      </div>
      <template v-else>
        <div v-if="loading" class="tc-empty">Loading…</div>
        <div v-else-if="denied" class="tc-empty tc-bad">Couldn't load comments.</div>
        <div v-else-if="!items.length" class="tc-empty">No comments yet — be the first.</div>

        <div class="tc-list">
          <div v-for="c in items" :key="c.id" class="tc-row">
            <div class="tc-av" :style="{ background: c.uid === track.artistId ? '#8b3a3a' : '#2e5b7e' }">{{ initials(c.name) }}</div>
            <div class="tc-main">
              <div class="tc-meta">
                <strong>{{ c.name }}</strong>
                <span v-if="c.uid === track.artistId" class="tc-badge">Artist</span>
                <span class="tc-time">{{ ago(c.createdAt) }}</span>
              </div>
              <div class="tc-text">{{ c.text }}</div>
            </div>
            <div class="tc-actions">
              <button v-if="canDelete(c)" type="button" @click="remove(c)">Delete</button>
              <button v-else-if="c.uid !== uid" type="button" @click="report(c)">{{ reported[c.id] ? "Reported" : "Report" }}</button>
            </div>
          </div>
        </div>

        <form class="tc-form" @submit.prevent="send">
          <input v-model="draft" maxlength="500" placeholder="Add a comment…" autocomplete="off" />
          <button type="submit" :disabled="!canSend">{{ sending ? "…" : "Post" }}</button>
        </form>
        <div v-if="error" class="tc-bad">{{ error }}</div>
      </template>
    </div>
  </div>
</template>

<script>
import { auth, db } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc, setDoc, onSnapshot, collection, query, orderBy, limit, addDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";

// Comments on a track: tracks/{trackId}/comments/{id} { uid, name, text<=500, createdAt }.
// Public tracks: any signed-in user. Members-only tracks: subscribers, the artist and admins.
// The server keeps tracks.commentCount in sync (functions: onTrackCommentCreated/Deleted).
export default {
  name: "TrackComments",
  props: {
    trackId: { type: String, required: true },
    showHeader: { type: Boolean, default: true },
    startOpen: { type: Boolean, default: false },
  },
  data() {
    return {
      open: this.startOpen, uid: null, isMember: false, isAdmin: false, myName: "",
      track: {}, items: [], loading: true, denied: false,
      draft: "", sending: false, error: "", reported: {},
      liked: false, likeAdj: 0, likeBusy: false,
    };
  },
  computed: {
    count() { return this.track.commentCount || 0; },
    locked() {
      const t = this.track;
      return t.visibility === "members" && !this.isMember && !this.isAdmin && t.artistId !== this.uid;
    },
    likeCount() { return Math.max(0, (this.track.likes || 0) + this.likeAdj); },
    canSend() { return !!this.uid && !this.sending && this.draft.trim().length > 0; },
  },
  watch: {
    trackId() { this.bindTrack(); this.bindLike(); },
    uid() { this.bindLike(); },
    open() { this.bindComments(); },
    locked() { this.bindComments(); },
  },
  mounted() {
    this._authOff = onAuthStateChanged(auth, (u) => {
      this.uid = u ? u.uid : null;
      if (this._userOff) { this._userOff(); this._userOff = null; }
      this.isMember = false; this.isAdmin = false;
      if (!u) return;
      this._userOff = onSnapshot(doc(db, "user", u.uid), (s) => {
        const d = s.data() || {};
        const type = String(d.type || d.role || "").toLowerCase();
        this.isMember = !!d.isMember || type === "admin";
        this.isAdmin = type === "admin";
        this.myName = this.nameOf(d, type);
      }, () => {});
    });
    this.bindTrack();
  },
  beforeDestroy() {
    if (this._authOff) this._authOff();
    if (this._userOff) this._userOff();
    if (this._trackOff) this._trackOff();
    if (this._likeOff) this._likeOff();
    if (this._cOff) this._cOff();
  },
  methods: {
    nameOf(d, type) {
      if (type === "band" && d.bandName) return d.bandName;
      if (["artist", "producer", "rising", "band"].includes(type) && d.stageName) return d.stageName;
      return d.fullName || d.name || "Muzilly member";
    },
    initials(n) { return (n || "?").split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2); },
    ago(ts) {
      const ms = ts && ts.toMillis ? ts.toMillis() : 0;
      if (!ms) return "Just now";
      const m = Math.floor((Date.now() - ms) / 60000);
      if (m < 1) return "Just now";
      if (m < 60) return m + "m ago";
      const h = Math.floor(m / 60);
      if (h < 24) return h + "h ago";
      return Math.floor(h / 24) + "d ago";
    },
    bindTrack() {
      if (this._trackOff) { this._trackOff(); this._trackOff = null; }
      this.track = {}; this.items = []; this.loading = true; this.denied = false;
      if (!this.trackId) return;
      this._trackOff = onSnapshot(doc(db, "tracks", this.trackId),
        (s) => {
          const next = s.exists() ? { id: s.id, ...s.data() } : {};
          if ((next.likes || 0) !== (this.track.likes || 0)) this.likeAdj = 0; // server caught up with the optimistic change
          this.track = next;
          this.bindComments();
        },
        () => {});
    },
    bindLike() {
      if (this._likeOff) { this._likeOff(); this._likeOff = null; }
      this.liked = false; this.likeAdj = 0;
      if (!this.uid || !this.trackId) return;
      // likes/{uid}_{trackId}: existence = liked
      this._likeOff = onSnapshot(doc(db, "likes", this.uid + "_" + this.trackId), (s) => { this.liked = s.exists(); }, () => {});
    },
    async toggleLike() {
      if (!this.uid || this.likeBusy || this.locked || !this.track.id) return;
      this.likeBusy = true;
      const ref = doc(db, "likes", this.uid + "_" + this.trackId);
      try {
        if (this.liked) { this.liked = false; this.likeAdj -= 1; await deleteDoc(ref); }
        else {
          this.liked = true; this.likeAdj += 1;
          await setDoc(ref, { uid: this.uid, trackId: this.trackId, artistId: this.track.artistId || "", createdAt: serverTimestamp() });
        }
      } catch (e) {
        console.error("like failed", e);
        this.liked = !this.liked; this.likeAdj = 0;
      } finally { this.likeBusy = false; }
    },
    bindComments() {
      if (this._cOff) { this._cOff(); this._cOff = null; }
      if (!this.open || this.locked || !this.track.id) return;
      this.loading = true; this.denied = false;
      const q = query(collection(db, "tracks", this.trackId, "comments"), orderBy("createdAt", "desc"), limit(100));
      this._cOff = onSnapshot(q, { includeMetadataChanges: false }, (snap) => {
        this.items = snap.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: "estimate" }) }));
        this.loading = false; this.denied = false;
      }, (e) => { console.warn("comments listener", e); this.loading = false; this.denied = true; });
    },
    canDelete(c) { return c.uid === this.uid || this.track.artistId === this.uid || this.isAdmin; },
    async send() {
      if (!this.canSend) return;
      this.sending = true; this.error = "";
      try {
        await addDoc(collection(db, "tracks", this.trackId, "comments"), {
          uid: this.uid, name: (this.myName || "Muzilly member").slice(0, 80),
          text: this.draft.trim().slice(0, 500), createdAt: serverTimestamp(),
        });
        this.draft = "";
      } catch (e) {
        console.error("comment failed", e);
        this.error = "Couldn't post your comment. Please try again.";
      } finally { this.sending = false; }
    },
    async remove(c) {
      try { await deleteDoc(doc(db, "tracks", this.trackId, "comments", c.id)); }
      catch (e) { console.error("delete comment failed", e); this.error = "Couldn't delete that comment."; }
    },
    async report(c) {
      if (this.reported[c.id] || !this.uid) return;
      try {
        await addDoc(collection(db, "flags"), {
          reporterId: this.uid, targetType: "comment", targetId: this.trackId + "/" + c.id, targetOwnerId: c.uid,
          reason: "Inappropriate content", details: String(c.text || "").slice(0, 500),
          status: "open", source: "web", createdAt: serverTimestamp(),
        });
        this.$set(this.reported, c.id, true);
      } catch (e) { console.error("report failed", e); }
    },
  },
};
</script>

<style scoped>
.tc{font-family:'DM Sans','Helvetica Neue',sans-serif;color:#f5f0e8}
.tc-head{display:flex;align-items:center;gap:14px;border-top:0.5px solid rgba(245,240,232,0.05);padding:7px 14px 12px}
.tc-like{background:none;border:none;color:rgba(245,240,232,0.5);font-size:13px;font-family:inherit;cursor:pointer;padding:0}
.tc-like.on{color:#e57373}
.tc-like:disabled{opacity:.4;cursor:default}
.tc-toggle{flex:1;display:flex;align-items:center;background:none;border:none;color:inherit;font-family:inherit;cursor:pointer;padding:0;text-align:left}
.tc-count{font-size:12px;color:rgba(212,140,28,0.9)}
.tc-chev{margin-left:auto;font-size:10px;color:rgba(245,240,232,0.3)}
.tc-body{padding:4px 14px 14px}
.tc-list{display:flex;flex-direction:column;gap:12px;max-height:340px;overflow-y:auto;margin-bottom:10px}
.tc-row{display:flex;gap:10px;align-items:flex-start}
.tc-av{width:30px;height:30px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;color:#fff}
.tc-main{flex:1;min-width:0}
.tc-meta{display:flex;align-items:center;gap:6px;font-size:12px}
.tc-meta strong{font-weight:600}
.tc-badge{font-size:9px;background:rgba(212,140,28,0.15);color:#d48c1c;border-radius:10px;padding:1px 7px}
.tc-time{color:rgba(245,240,232,0.35);font-size:11px}
.tc-text{font-size:13.5px;color:rgba(245,240,232,0.85);margin-top:2px;white-space:pre-wrap;word-break:break-word}
.tc-actions button{background:none;border:none;color:rgba(245,240,232,0.3);font-size:11px;cursor:pointer;font-family:inherit}
.tc-actions button:hover{color:#e57373}
.tc-form{display:flex;gap:8px}
.tc-form input{flex:1;background:rgba(245,240,232,0.06);border:0.5px solid rgba(245,240,232,0.12);border-radius:18px;padding:9px 14px;color:#f5f0e8;font-size:13px;font-family:inherit;outline:none}
.tc-form input:focus{border-color:rgba(212,140,28,0.6)}
.tc-form button{background:#d48c1c;color:#0c0a0e;border:none;border-radius:18px;padding:0 18px;font-weight:600;font-size:13px;cursor:pointer;font-family:inherit}
.tc-form button:disabled{opacity:.4;cursor:default}
.tc-empty{font-size:12.5px;color:rgba(245,240,232,0.4);padding:6px 0 10px}
.tc-bad{font-size:12px;color:#e57373;margin-top:6px}
.tc-locked{font-size:12.5px;color:rgba(245,240,232,0.55);padding:6px 0}
.tc-link{color:#d48c1c;cursor:pointer;text-decoration:underline}
</style>